import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, List, Avatar, Spin } from 'antd';
import {
  TeamOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import { operationsDashboardService } from '@iso/services';
import { StatCard, SectionCard, EmptyBox, PageTitle, OPS_COLORS, initials } from './OperationsUI';

// Operations Dashboard — an at-a-glance summary that aggregates live data from
// the other Operations modules (Time Tracker, Tasks, Team Schedule) via a
// single server-side aggregate endpoint.

// Colour an activity dot from keywords in its label (best-effort, matching the
// Figma's green / amber / neutral treatment).
const activityColor = (label) => {
  const l = (label || '').toLowerCase();
  if (/(alert|reminder|overdue|break|paused|due)/.test(l)) return OPS_COLORS.orange;
  if (/(clocked in|completed|approved|generated|submitted)/.test(l)) return OPS_COLORS.greenBright;
  return OPS_COLORS.grey;
};

const OperationsDashboard = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activePropertyId) return;
    let cancelled = false;
    setLoading(true);
    operationsDashboardService
      .getOverview(activePropertyId)
      .then((res) => {
        if (!cancelled) setData(res?.data || null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activePropertyId]);

  const activeWorkers = data?.active_workers || {};
  const openTasks = data?.open_tasks || {};
  const todaysShifts = data?.todays_shifts || {};
  const hours = data?.hours_this_week || {};
  const activity = data?.todays_activity || [];
  const upcoming = data?.upcoming_this_week || [];

  return (
    <LayoutWrapper style={{ display: 'block' }}>
      <PageTitle title="Operations" subtitle="Dashboard" />

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Active Workers"
              value={activeWorkers.count || 0}
              sub="Currently clocked in"
              icon={<TeamOutlined />}
              accent={OPS_COLORS.green}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Open Tasks"
              value={openTasks.count || 0}
              sub={`${openTasks.overdue || 0} overdue`}
              subColor={openTasks.overdue ? OPS_COLORS.orange : undefined}
              icon={<ExclamationCircleOutlined />}
              accent={OPS_COLORS.orange}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Today's Shifts"
              value={todaysShifts.count || 0}
              sub={`${todaysShifts.unconfirmed || 0} unconfirmed`}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Hours This Week"
              value={hours.worked || 0}
              sub={`vs ${hours.scheduled || 0} scheduled`}
              icon={<ClockCircleOutlined />}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 16 }}>
          <Col xs={24} lg={15}>
            <SectionCard title="Today's Activity" fill style={{ minHeight: 'calc(100vh - 300px)' }} bodyStyle={{ padding: activity.length ? '0 8px' : 0 }}>
              {activity.length === 0 ? (
                <EmptyBox text="No activity today" />
              ) : (
                <List
                  dataSource={activity}
                  renderItem={(event) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{ background: activityColor(event.label) }}
                            icon={<ClockCircleOutlined />}
                          />
                        }
                        title={<span style={{ fontWeight: 500 }}>{event.label}</span>}
                        description={event.time ? moment(event.time).format('h:mm A') : null}
                      />
                    </List.Item>
                  )}
                />
              )}
            </SectionCard>
          </Col>

          <Col xs={24} lg={9}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
              <SectionCard title="Upcoming This Week" bodyStyle={{ padding: upcoming.length ? '0 8px' : 0 }}>
                {upcoming.length === 0 ? (
                  <EmptyBox text="Nothing upcoming" />
                ) : (
                  <List
                    dataSource={upcoming}
                    renderItem={(item) => (
                      <List.Item
                        extra={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ color: OPS_COLORS.grey, whiteSpace: 'nowrap', fontSize: 13 }}>
                              {item.date ? moment(item.date).format('ddd D MMM') : null}
                            </span>
                            {item.assignee ? (
                              <Avatar size="small" style={{ background: OPS_COLORS.brand }}>
                                {initials(item.assignee)}
                              </Avatar>
                            ) : null}
                          </div>
                        }
                      >
                        <List.Item.Meta
                          title={<span style={{ fontWeight: 600 }}>{item.title}</span>}
                          description={item.assignee || null}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </SectionCard>

              <div style={{ flex: 1, display: 'flex' }}>
                <SectionCard title="Alerts" fill style={{ width: '100%' }} bodyStyle={{ padding: 0 }}>
                  <EmptyBox text="No active alerts" />
                </SectionCard>
              </div>
            </div>
          </Col>
        </Row>
      </Spin>
    </LayoutWrapper>
  );
};

export default OperationsDashboard;
